"use client"

import { useMemo, useCallback } from "react"
import type { SurfSpot } from "@/lib/types/surf-spot"
import { calculateDistance } from "@/lib/utils/geolocation"
import { createSurfSpotSlug } from "@/lib/utils/slug"
import { useDataFetch } from "./use-data-fetch"

// Extended SurfSpot type with optional distance property
interface SurfSpotWithDistance extends SurfSpot {
  distance?: number
}

interface UseSurfSpotsReturn {
  spots: SurfSpotWithDistance[]
  loading: boolean
  error: string | null
  refetch: () => void
}

interface UseSurfSpotsOptions {
  userLatitude?: number | null
  userLongitude?: number | null
  maxDistance?: number | null
  sortByDistance?: boolean
  limit?: number
}

interface SurfSpotsApiResponse {
  success: boolean
  data?: {
    spots: SurfSpot[]
    count: number
  }
  error?: string
}

// The transform receives just the data part (not the full response)
// because useDataFetch extracts result.data before passing to transform
interface SurfSpotsDataInput {
  spots: SurfSpot[]
  count: number
}

// The transform returns spots with optional distance property
interface SurfSpotsDataOutput {
  spots: SurfSpotWithDistance[]
  count: number
}

/**
 * Hook for fetching surf spots with optional distance filtering and sorting
 * 
 * Uses the base useDataFetch hook for consistent error handling and retry logic
 * Note: This endpoint doesn't require authentication (public data)
 */
export function useSurfSpots(options: UseSurfSpotsOptions = {}): UseSurfSpotsReturn {
  const { userLatitude, userLongitude, maxDistance, sortByDistance, limit = 100 } = options

  // Memoize transform function to prevent unnecessary re-fetches
  // Transform function identity changes cause fetchData to be recreated, triggering re-fetches
  // Note: useDataFetch extracts result.data before passing to transform, so we receive { spots, count }
  const transformSpots = useCallback((responseData: SurfSpotsDataInput): SurfSpotsDataOutput => {
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots transform: Input responseData:", responseData)
    }
    // useDataFetch extracts result.data, so we receive { spots, count } directly
    const spots = responseData?.spots || []
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots transform: Extracted spots from responseData:", spots)
    }
    // Ensure all spots have slugs (generate if missing)
    let processedSpots: SurfSpotWithDistance[] = spots.map((spot) => ({
      ...spot,
      slug: spot.slug || createSurfSpotSlug(spot.name, spot.location, spot.country),
    }))
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots transform: Processed spots:", processedSpots)
    }

        // Calculate distance if user location is provided
        if (
          userLatitude !== null &&
          userLatitude !== undefined &&
          userLongitude !== null &&
          userLongitude !== undefined
        ) {
          // Calculate distance for each spot
          processedSpots = processedSpots.map((spot) => ({
            ...spot,
            distance: calculateDistance(
              userLatitude,
              userLongitude,
              Number(spot.latitude),
              Number(spot.longitude)
            ),
          }))

          // Filter by max distance if specified
          if (maxDistance !== null && maxDistance !== undefined) {
            processedSpots = processedSpots.filter(
          (spot) => spot.distance !== undefined && spot.distance <= maxDistance
            )
          }

          // Sort by distance if requested
          if (sortByDistance) {
            processedSpots.sort((a, b) => (a.distance || 0) - (b.distance || 0))
          }
        }

        // Return in format expected by component
    // The transform result is stored directly as data, so we return { spots, count }
    const result: SurfSpotsDataOutput = {
            spots: processedSpots,
            count: processedSpots.length,
    }
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots transform: Returning result:", result)
    }
    return result
  }, [userLatitude, userLongitude, maxDistance, sortByDistance])

  // Fetch raw spots data using base hook
  // This endpoint is public, so no auth needed
  // The generic type is SurfSpotsDataOutput because transform returns that type
  const { data, loading, error, refetch } = useDataFetch<SurfSpotsDataOutput>(
    `/api/surf-spots?limit=${limit}`,
    {
      // Transform the API response and apply distance calculations
      transform: transformSpots,
      // Public endpoint - no auth required
      waitForAuth: false,
      retryOn401: false,
      initialDelay: 0,
      timeout: 10000, // Shorter timeout for public data
      retryDelay: 500,
      debug: process.env.NODE_ENV === "development",
    }
  )

  // Extract spots from transformed data
  // The transform returns { spots, count }, which is stored directly as data
  // Use useMemo to avoid recalculating when options change (transform already handles it)
  const spots = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots: Raw data from hook:", data)
      console.log("useSurfSpots: data.spots:", data?.spots)
      console.log("useSurfSpots: Array.isArray(data.spots):", Array.isArray(data?.spots))
    }
    // The transform function returns { spots, count }, which is stored directly as data
    const extractedSpots: SurfSpotWithDistance[] = data?.spots || []
    if (process.env.NODE_ENV === "development") {
      console.log("useSurfSpots: Final extracted spots:", extractedSpots)
    }
    return extractedSpots
  }, [data])

  return {
    spots,
    loading,
    error,
    refetch,
  }
}
